<template>
  <div class="trade-ticket-container">
    <loading ref="loading"></loading>
    <v-dialog v-model="preSettlementTermsDialog" max-width="520" persistent>
      <v-card>
        <v-card-title>Pre-Settlement Payment Program Terms</v-card-title>
        <v-card-text>
          Before using Pre-Settlement Payment on this trade, a principal/owner must
          sign the program terms.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelPreSettlementTerms">Cancel</v-btn>
          <v-btn color="primary" @click="goToPreSettlementTerms">
            Go to Terms
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card class="elevation-0" tile style="min-height: 100vh">
      <v-toolbar dark color="primary">
        <v-toolbar-title>
          <v-icon
            class="mr-2"
            :style="`transform: rotate(35deg);`"
            color="secondary"
          >
            mdi-ticket
          </v-icon>
          <span v-if="sell.items.length > 0 || buy.items.length > 0">
            {{ type }} Trade Ticket
          </span>
          <span v-else>Trade Ticket</span>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <div v-if="sell.items.length > 0 || buy.items.length > 0" class="d-flex align-center">
          <v-progress-circular
            :rotate="360"
            :size="40"
            :width="3"
            :value="
              (secondsRemaining * 100) / (SPOT_PRICE_UPDATE_INTERVAL / 1000)
            "
            :color="timerColor(secondsRemaining)"
            style="margin-right: 5px"
          >
            <span class="white--text">{{ secondsRemaining }}s</span>
          </v-progress-circular>
          <span class="grey--text d-none d-sm-flex"
            >Prices refresh every
            {{ SPOT_PRICE_UPDATE_INTERVAL / 1000 }} seconds.</span
          >
          <span class="grey--text d-flex d-sm-none caption"
            >Refresh: {{ SPOT_PRICE_UPDATE_INTERVAL / 1000 }} seconds</span
          >
        </div>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <template v-if="sell.items.length === 0 && buy.items.length === 0">
          <div class="text-center pt-12 mb-4">
            <v-icon
              :style="`transform: rotate(35deg);`"
              color="secondary"
              size="100"
            >
              mdi-ticket
            </v-icon>
            <div class="text-h5 font-weight-bold mb-4">
              Your trade ticket is empty.
            </div>
            <div class="text-subtitle-1 mb-4">
              Click the "Buy" or "Sell" buttons within the product catalog to
              add to your trade ticket.
            </div>
            <v-btn outlined @click="close"> Continue Browsing</v-btn>
          </div>
        </template>
        <template v-else>
          <v-tabs
            v-if="buy.items.length > 0 && sell.items.length > 0"
            v-model="buySellTabs"
            centered
            class="trade-tabs"
          >
            <v-tab v-if="buy.items.length > 0" key="buy" href="#tab-buy"
            >
              <v-badge color="secondary" inline :content="buy.items.length" small
              ><span class="font-weight-bold">Buy Order</span></v-badge
              >
            </v-tab>
            <v-tab v-if="sell.items.length > 0" key="sell" href="#tab-sell"
            >
              <v-badge color="secondary" inline :content="sell.items.length" small
              ><span class="font-weight-bold">Sell Order</span></v-badge
              >
            </v-tab
            >
          </v-tabs>
          <v-tabs-items v-model="buySellTabs">
            <v-tab-item
              value="tab-buy"
              v-if="buy.items.length > 0 && Object.keys(spotPrices).length > 0"
            >
              <trade-form
                buy-sell="buy"
                :shown="this.shown && buySellTabs === 'tab-buy'"
                v-on:executeTrade="confirmThenExecuteTrade"
                v-on:close="close"
                v-on:go-to-pre-settlement-program="goToPreSettlementTerms"
              >
              </trade-form>
            </v-tab-item>
            <v-tab-item
              value="tab-sell"
              v-if="sell.items.length > 0 && Object.keys(spotPrices).length > 0"
            >
              <trade-form
                buy-sell="sell"
                :shown="this.shown && buySellTabs === 'tab-sell'"
                v-on:executeTrade="confirmThenExecuteTrade"
                v-on:close="close"
                v-on:go-to-pre-settlement-program="goToPreSettlementTerms"
              >
              </trade-form>
            </v-tab-item>
          </v-tabs-items>
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import productMixin from '@/mixins/product'
import eventTransmitter from '@/mixins/eventTransmitter'
import { EVENT_PROVIDERS, ANALYTICS_EVENTS } from '@/util/constants'
import {mapGetters, mapState} from 'vuex'
import Loading from '@/components/Loading'
import TradeForm from '@/components/cart/TradeForm'
import {requiresPreSettlementTerms} from '@/util/preSettlement'

const SPOT_PRICE_UPDATE_INTERVAL = 30000
const MAX_SPOT_PRICE_AGE_S = 45

export default {
  components: {
    TradeForm,
    Loading,
  },
  async mounted() {
    await Promise.all([
      this.chooseTab(),
      this.spotPriceUpdater(),
      this.$store.dispatch('fetchConfig')
    ])
  },
  created() {
    window._VMA.$on('AUTH_FAILED', () => {
      clearTimeout(this.spotPriceTimeout)
      this.online = false
    })
    window._VMA.$on('LOGOUT', () => {
      clearTimeout(this.spotPriceTimeout)
      this.online = false
    })
    this.$on('OFFLINE', () => {
      clearTimeout(this.spotPriceTimeout)
      this.online = false
    })
    this.$on('ONLINE', () => {
      this.spotPriceUpdater()
    })
  },
  mixins: [productMixin, eventTransmitter],
  data() {
    return {
      buySellTabs: 'tab-buy',
      online: true,
      secondsRemaining: SPOT_PRICE_UPDATE_INTERVAL / 1000,
      spotPriceTimeout: null,
      preSettlementTermsDialog: false,
      SPOT_PRICE_UPDATE_INTERVAL
    }
  },
  computed: {
    ...mapState({
      spotPrices: (state) => state.user.tradeTicketSpotPrices,
    }),
    ...mapState({
      buy: (state) => state.cart.buy,
      sell: (state) => state.cart.sell,
    }),
    ...mapGetters([
      `getSalesOrderByConfirmation`,
      `getPurchaseOrderByConfirmation`,
      `getConfig`,
      'getSelectedTradingRole',
      'getSelectedCompany',
    ]),
    type: {
      get() {
        return this.buySellTabs === `tab-buy` ? 'Buy' : 'Sell'
      },
    },
  },
  props: ['shown', 'defaultBuySell'],
  watch: {
    buySellTabs: {
      async handler() {
        await this.sendEvent()
      },
    },
    spotPrices: {
      async handler(newPrices, oldPrices) {
        // Send event when spot prices become available and cart is visible
        const hadPrices = oldPrices && Object.keys(oldPrices).length > 0 && oldPrices['Gold'] !== undefined
        const hasPrices = newPrices && Object.keys(newPrices).length > 0 && newPrices['Gold'] !== undefined
        if (!hadPrices && hasPrices && this.shown) {
          await this.sendEvent()
        }
      },
    },
    shown: {
      async handler(newValue) {
        if (newValue) {
          if (this.defaultBuySell) this.buySellTabs = `tab-${this.defaultBuySell}`
          this.spotPriceUpdater()
          // Send event when cart becomes visible
          await this.sendEvent()
        }
      },
    },
    buy: {
      handler() {
        this.chooseTab()
      },
    },
    sell: {
      handler() {
        this.chooseTab()
      },
    },
  },
  beforeDestroy() {
    clearTimeout(this.spotPriceTimeout)
  },
  methods: {
    async sendEvent() {
      // Spot prices not loaded yet
      if (this.spotPrices['Gold'] === undefined) return
      const bidAsk = (this.buySellTabs === 'tab-sell' ? 'bid' : 'ask')
      const items = (this.buySellTabs === 'tab-sell' ? this.sell.items : this.buy.items).map(item => {
        return {
          item_id: item.item.netsuite_internalId,
          item_name: item.item.name,
          price: this.computePrices(
            item.item,
            this.spotPrices,
            this.getSelectedCompany,
            this.getSelectedTradingRole
          )[bidAsk],
          quantity: item.quantity
        }
      })
      if (items.length > 0) {
        const cartTotal = items.reduce((acc, item) => acc + item.price * item.quantity, 0)
        await this.transmitEvent([EVENT_PROVIDERS.GA, EVENT_PROVIDERS.LOGZIO], {
          eventName: ANALYTICS_EVENTS.VIEW_CART,
          bid_ask: bidAsk,
          currency: "USD",
          value: cartTotal,
          items,
        })
      }
    },
    spotPricesAgeSeconds() {
      const adjustment =
        parseFloat(this.getConfig('SERVER_CLIENT_TIMESTAMP_DIFFERENCE')) * 1000
      if (this.spotPrices !== null && Object.keys(this.spotPrices).length > 0) {
        const minTimestamp = Math.min(
          ...Object.values(this.spotPrices).map((x) =>
            new Date(x.createdAt).getTime()
          )
        )
        return (new Date().getTime() - adjustment - minTimestamp) / 1000
      }
      return Infinity
    },
    validateRecentSpotPrices() {
      return this.spotPricesAgeSeconds() < MAX_SPOT_PRICE_AGE_S
    },
    timerColor(secondsRemaining) {
      if (secondsRemaining <= 5) return `red`
      return `black`
    },
    async spotPriceUpdater() {
      if (this.spotPriceTimeout) clearInterval(this.spotPriceTimeout)
      
      // calculate time until next update
      const timeSinceLastUpdate = this.spotPricesAgeSeconds()
      const timeUntilNextUpdate = Math.max(0, SPOT_PRICE_UPDATE_INTERVAL / 1000 - timeSinceLastUpdate)
      
      if (timeUntilNextUpdate <= 0 || Object.keys(this.spotPrices).length === 0) {
        try {
          await this.$store.dispatch('getTradeTicketSpotPrice')
          this.online = true
          this.secondsRemaining = SPOT_PRICE_UPDATE_INTERVAL / 1000
        } catch (e) {
          this.online = false
          this.secondsRemaining = 0
        }
      } else {
        this.secondsRemaining = Math.floor(timeUntilNextUpdate)
      }
      
      // start interval timer
      this.spotPriceTimeout = setInterval(async () => {
        if (this.online) {
          this.secondsRemaining = Math.max(0, this.secondsRemaining - 1)
          
          // if we've reached 0, fetch new prices
          if (this.secondsRemaining === 0) {
            try {
              await this.$store.dispatch('getTradeTicketSpotPrice')
              this.secondsRemaining = SPOT_PRICE_UPDATE_INTERVAL / 1000
            } catch (e) {
              this.online = false
            }
          }
        }
      }, 1000)
    },
    async chooseTab() {
      // If there are any items in the cart, make sure we are on a tab that has items
      const previousTab = this.buySellTabs
      if (this.buy.items.length > 0 || this.sell.items.length > 0) {
        if (this.buySellTabs === `tab-buy` && this.buy.items.length === 0) {
          this.buySellTabs = 'tab-sell'
        }
        if (this.buySellTabs === `tab-sell` && this.sell.items.length === 0) {
          this.buySellTabs = 'tab-buy'
        }
      }
      // If tab changed, watcher will handle sending event
      // If tab didn't change but items did, send event now
      if (this.shown && this.buySellTabs === previousTab) {
        await this.sendEvent()
      }
    },
    close() {
      this.$emit('close')
    },
    async confirmThenExecuteTrade(buySell, confirmationNumber) {
      if (
        buySell === 'sell' &&
        requiresPreSettlementTerms(
          this.getSelectedCompany,
          this.sell.selectedPaymentMethod,
          this.getConfig('PRE_SETTLEMENT_TERM_IDS')
        )
      ) {
        this.preSettlementTermsDialog = true
        return
      }

      // fire begin_checkout
      const cartItems = (buySell === 'buy' ? this.buy.items : this.sell.items).map(ci => ({
        item_id: ci.item.netsuite_internalId,
        item_name: ci.item.name,
        quantity: ci.quantity,
        price: this.computePrices(
          ci.item,
          this.spotPrices,
          this.getSelectedCompany,
          this.getSelectedTradingRole
        )[buySell === 'buy' ? 'ask' : 'bid']
      }))
      const cartValue = cartItems.reduce((acc,i)=>acc+i.price*i.quantity,0)
      await this.transmitEvent([EVENT_PROVIDERS.GA, EVENT_PROVIDERS.LOGZIO], {
        eventName: ANALYTICS_EVENTS.BEGIN_CHECKOUT,
        cart_value: cartValue,
        currency: 'USD',
        item_count: cartItems.length,
      })
      window._VMA.$emit('CONFIRM', {
        title: `Confirm Trade`,
        message: `Are you sure you want to execute this trade?`,
        options: {},
        callback: (agree) => {
          if (agree) this.executeTrade(buySell, confirmationNumber)
        },
      })
    },
    cancelPreSettlementTerms() {
      this.preSettlementTermsDialog = false
    },
    goToPreSettlementTerms() {
      this.cancelPreSettlementTerms()
      this.$emit('close')
      this.$router.push({path: '/resources/pre-settlement-payment-program'})
    },
    async executeTrade(buySell, confirmationNumber) {
      if (!this.validateRecentSpotPrices()) {
        window._VMA.$emit('CONFIRM', {
          title: `Spot Prices Expired`,
          message: `<p>We are having trouble fetching the current spot prices. Click <strong>RETRY</strong> to refresh prices.</p><p>If this does not work, please check your internet connection, try reloading the page, or wait for the timer to reset.</p><p>If you continue to have trouble, please contact the trading desk.</p>`,
          options: {width: 500, confirmText: `Retry`},
          callback: async (agree) => {
            if (agree) {
              this.secondsRemaining = 0
              await this.spotPriceUpdater()
            }
          },
        })
        this.log({
          message: `Trade ticket spot price timer failed`,
          spotPrices: this.spotPrices,
          spotPricesAgeSeconds: this.spotPricesAgeSeconds(),
          spotPriceTimerOnline: this.online,
          isError: true,
          success: false,
        })
      } else {
        window._VMA.$emit('DISMISS_SNACKBAR')
        this.$refs.loading.open(
          'Executing Trade...',
          'Please do not navigate away from this window until your order is fully executed and you receive a confirmation number.',
          {
            color: 'primary',
          }
        )
        try {
          if (buySell === 'buy') {
            await this.$store.dispatch(`submitSalesOrder`, {
              spotPrices: this.spotPrices,
              confirmationNumber,
            })
            
            // purchase analytics is emitted in order.js after successful server response
            await this.$store.dispatch('resetCartState', {buySell})
            this.$refs.loading.close()
            this.close()
            window._VMA.$emit('NEW_ORDER')
            await this.$router.push(
              `/orders/confirmation/so/${this.getSalesOrderByConfirmation.confirmationNumber}`
            )
          } else {
            await this.$store.dispatch(`submitPurchaseOrder`, {
              spotPrices: this.spotPrices,
              confirmationNumber,
            })
            // purchase analytics is emitted in order.js after successful server response
            await this.$store.dispatch('resetCartState', {buySell})
            this.$refs.loading.close()
            this.close()
            window._VMA.$emit('NEW_ORDER')
            await this.$router.push(
              `/orders/confirmation/po/${this.getPurchaseOrderByConfirmation.confirmationNumber}`
            )
          }
        } catch (e) {
          this.$refs.loading.close()
          this.close()
          this.log({
            message: `Trade execution failed`,
            error: e,
            isError: true,
            success: false,
          })
        }
      }
    },
  },
}
</script>

<style>
.trade-ticket-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  background-color: white;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.trade-tabs {
  margin-bottom: 16px;
}

.trade-tabs .v-tab {
  margin: 0 2px;
  padding: 12px 24px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 0;
  text-align: center !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-height: 36px;
  height: auto;
}

.trade-tabs .v-tab--active {
  color: inherit !important;
  background: none !important;
  box-shadow: none !important;
  border: 2px solid rgba(0, 0, 0, 0.87) !important;
}

.trade-tabs .v-tab--active .v-tab__wrapper {
  font-weight: 700 !important;
}

.trade-tabs .v-tab--active .v-tab__wrapper span {
  font-weight: 700 !important;
}

.trade-tabs .v-tabs-slider {
  display: none !important;
}

@media (max-width: 600px) {
  .trade-tabs {
    margin-bottom: 8px;
  }
  
  .v-toolbar__title {
    font-size: 1.1rem;
  }
}
</style>